/**
 * Hillshade — per-cell shading for pseudo-3D terrain look (v0M2.6d Level 1).
 *
 * The strategic map is 2D but we want mountains to *appear* raised,
 * seas to sink, hills to have a visible north-west-facing sunlit side.
 * Standard cartographic hillshading achieves this cheaply — no true
 * 3D, no ray tracing, no assets — by modulating cell brightness based
 * on the elevation gradient to the light direction.
 *
 * Convention: light comes from the **north-west** (top-left of the
 * canvas). Cells that rise toward the NW catch the light and brighten;
 * cells that fall away (SE facing slopes) go into shadow.
 *
 * Future work (not v0M2.6d):
 *  - Level 2 — procedural terrain glyphs at max zoom (mountain peak
 *    triangles, hill bumps, forest tree symbols, farmland furrows)
 *  - Level 3 — full cartographic scroll style (river bloom, coastal
 *    fade, parametric city roofs, brush-stroke province borders)
 *  Both flagged as v0M3+ candidates; the elevation table + shading
 *  math established here transfer directly into those layers.
 */

import type { LoadedMap } from './map-loader';
import { terrainElevation } from './terrain-palette';

/** Max fractional brightness delta from slope shading. */
export const SHADE_AMPLITUDE = 0.45;

/**
 * Max fractional brightness delta from absolute elevation. This makes
 * mountains look intrinsically brighter (raised) and seas darker
 * (recessed), regardless of local gradient — the missing piece that
 * makes plateaus and ridges read as "3D" even in their interior.
 */
export const ELEVATION_AMPLITUDE = 0.30;

/** Distance in cells to sample the light-side neighbours for slope. */
const NEIGHBOUR_REACH = 3;

/**
 * Compute a signed shading value in [-1, 1] for cell (x, y).
 *
 * Positive = the cell rises toward the NW light source (bright side).
 * Negative = the cell falls away toward the SE (shadow side).
 * Zero    = the cell is level with its NW neighbours.
 *
 * The formula compares this cell's elevation against the average of
 * its NW / N / W neighbours (the light-source directions). The result
 * is clamped to [-1, 1] so the caller can multiply directly by
 * SHADE_AMPLITUDE without overshooting.
 */
export function cellShading(map: LoadedMap, x: number, y: number): number {
  const idx = y * map.width + x;
  const here = terrainElevation(map.terrain[idx]!);

  // Sample the NW / N / W side (light source) and the SE / S / E side
  // (shadow side) at NEIGHBOUR_REACH cells distance. Comparing the two
  // gives a true directional slope — this cell's slope toward the NW
  // light. Reaching 3 cells means even the interior of a modestly-sized
  // terrain feature still shows a gradient.
  const r = NEIGHBOUR_REACH;
  const lightOffsets: readonly [number, number][] = [
    [-r, -r],
    [0, -r],
    [-r, 0],
  ];
  const shadowOffsets: readonly [number, number][] = [
    [r, r],
    [0, r],
    [r, 0],
  ];

  let lightSum = 0;
  let lightN = 0;
  for (const [dx, dy] of lightOffsets) {
    const nx = x + dx;
    const ny = y + dy;
    if (nx >= 0 && ny >= 0 && nx < map.width && ny < map.height) {
      lightSum += terrainElevation(map.terrain[ny * map.width + nx]!);
      lightN++;
    }
  }
  let shadowSum = 0;
  let shadowN = 0;
  for (const [dx, dy] of shadowOffsets) {
    const nx = x + dx;
    const ny = y + dy;
    if (nx >= 0 && ny >= 0 && nx < map.width && ny < map.height) {
      shadowSum += terrainElevation(map.terrain[ny * map.width + nx]!);
      shadowN++;
    }
  }
  const lightAvg = lightN > 0 ? lightSum / lightN : here;
  const shadowAvg = shadowN > 0 ? shadowSum / shadowN : here;

  // Positive if the cell rises toward the light and drops toward the
  // shadow (SW-to-NE slope). Gain of 1.2 pushes typical transitions
  // toward the ±1 clamp.
  const raw = (shadowAvg - lightAvg) * 1.2;
  return raw < -1 ? -1 : raw > 1 ? 1 : raw;
}

/**
 * Absolute-elevation brightness. Independent of slope; captures the
 * "mountains are higher than plains, plains are higher than sea"
 * feeling that slope shading alone misses inside contiguous terrain.
 * Returns a signed value in roughly [-1, 1] (mountain 1.0 → +1, sea
 * −0.45 → −0.45).
 */
export function cellElevationBrightness(map: LoadedMap, x: number, y: number): number {
  const idx = y * map.width + x;
  const e = terrainElevation(map.terrain[idx]!);
  return e < -1 ? -1 : e > 1 ? 1 : e;
}

/**
 * Apply combined slope + elevation shading to an RGB triplet.
 * Multiplicative; clamps each channel into [0, 255]. Combines cleanly
 * with the noise pass because both are per-cell brightness modulations.
 */
export function applyShadingToRgb(
  r: number,
  g: number,
  b: number,
  slope: number,
  elevationBrightness: number,
): [number, number, number] {
  const mult = 1 + slope * SHADE_AMPLITUDE + elevationBrightness * ELEVATION_AMPLITUDE;
  return [
    Math.max(0, Math.min(255, Math.round(r * mult))),
    Math.max(0, Math.min(255, Math.round(g * mult))),
    Math.max(0, Math.min(255, Math.round(b * mult))),
  ];
}
